// services/healthService.js
// Lecturas de Firestore + llamadas de registro a Odoo para el Mood Monitor.
// Réplica del stream chain del widget Flutter mood_monitor_widget.dart.

import { db, collection, query, where, getDocs } from './FirebaseService';
import { orderBy, limit } from 'firebase/firestore';

// Mismo dispatcher + credenciales que usan scheduleService/doctorService y el
// cliente Flutter (OdooAPIGroup → `${baseUrl}/odoo-import`). El endpoint
// marketing-manager NO tiene whitelisteados los métodos de dais.health.daily.plan.
const ODOO_DISPATCHER_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN;
const ACCOUNT_ID = parseInt(process.env.REACT_APP_ACCOUNT_ID, 10) || 1;

/** Fecha local de hoy en formato yyyy-MM-dd. */
const todayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const mapDocs = (snap) => snap.docs.map((d) => ({ firestoreId: d.id, ...d.data() }));

/**
 * 1. Resuelve la health card `emotional_state` visible (primer match).
 *    Espejo de: where code == 'emotional_state' AND show == true, primer doc.
 * @returns {Promise<object|null>}
 */
export const fetchEmotionalStateCard = async () => {
  try {
    const q = query(
      collection(db, 'dais_health_card'),
      where('code', '==', 'emotional_state'),
      where('show', '==', true)
    );
    const snap = await getDocs(q);
    const cards = mapDocs(snap);
    return cards.length ? cards[0] : null;
  } catch (error) {
    console.warn('[healthService] fetchEmotionalStateCard error:', error);
    return null;
  }
};

/**
 * 2. Plan diario de hoy para el usuario y card (o null si no existe).
 *    where user_id == userId AND card_id == cardId AND date == hoy, orderBy id desc, limit 1.
 * @returns {Promise<object|null>}
 */
export const fetchTodayDailyPlan = async (userId, cardId) => {
  try {
    const q = query(
      collection(db, 'dais_health_daily_plan'),
      where('user_id', '==', userId),
      where('card_id', '==', cardId),
      where('date', '==', todayYmd()),
      orderBy('id', 'desc'),
      limit(1)
    );
    const snap = await getDocs(q);
    const plans = mapDocs(snap);
    return plans.length ? plans[0] : null;
  } catch (error) {
    console.warn('[healthService] fetchTodayDailyPlan error:', error);
    return null;
  }
};

/**
 * 3. Registro existente del plan diario (o null). Si existe => ya registró hoy.
 *    where daily_plan_id == dailyPlanId AND user_id == userId AND card_id == cardId,
 *    orderBy id desc, limit 1.
 * @returns {Promise<object|null>}
 */
export const fetchExistingRecord = async (dailyPlanId, userId, cardId) => {
  try {
    const q = query(
      collection(db, 'dais_health_record'),
      where('daily_plan_id', '==', dailyPlanId),
      where('user_id', '==', userId),
      where('card_id', '==', cardId),
      orderBy('id', 'desc'),
      limit(1)
    );
    const snap = await getDocs(q);
    const records = mapDocs(snap);
    return records.length ? records[0] : null;
  } catch (error) {
    console.warn('[healthService] fetchExistingRecord error:', error);
    return null;
  }
};

/**
 * 4. Grupos de emociones (preguntas simple_choice de la card).
 *    where card_id == cardId AND question_type == 'simple_choice'. Ordena por sequence.
 * @returns {Promise<object[]>}
 */
export const fetchEmotionGroups = async (cardId) => {
  try {
    const q = query(
      collection(db, 'dais_health_questions'),
      where('card_id', '==', cardId),
      where('question_type', '==', 'simple_choice')
    );
    const snap = await getDocs(q);
    const groups = mapDocs(snap);
    return groups.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  } catch (error) {
    console.warn('[healthService] fetchEmotionGroups error:', error);
    return [];
  }
};

// ─── Registro vía dispatcher Odoo (espejo de _registerEmotion en Flutter) ───

/**
 * Paso 1: obtiene el id del plan diario para agregar el registro.
 * @returns {Promise<number|null>}
 */
export const getDailyPlanId = async ({ userId, cardId, authToken }) => {
  try {
    const res = await fetch(ODOO_DISPATCHER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        params: {
          access_token: ACCESS_TOKEN,
          account_id: ACCOUNT_ID,
          auth_token: authToken || '',
          res_model: 'dais.health.daily.plan',
          res_method: 'get_daily_plan_by_user',
          res_params: { user_id: userId, card_id: cardId },
        },
      }),
    });
    const json = await res.json();
    const result = json?.result ?? json;
    // Flutter getter: $.result.data.id
    const id =
      result?.data?.id ??
      result?.data?.daily_plan_id ??
      result?.daily_plan_id ??
      result?.id ??
      null;
    return id != null ? id : null;
  } catch (error) {
    console.warn('[healthService] getDailyPlanId error:', error);
    return null;
  }
};

/**
 * Paso 2: agrega el registro de la emoción al plan diario.
 * @param {object} args
 * @param {number} args.dailyPlanId
 * @param {number} args.questionId  id de la pregunta (grupo)
 * @param {number} args.answerId    id de la respuesta (value_options)
 * @param {string} args.authToken
 * @returns {Promise<{success:boolean}>}
 */
export const addRecordToDailyPlan = async ({ dailyPlanId, questionId, answerId, authToken }) => {
  try {
    const res = await fetch(ODOO_DISPATCHER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        params: {
          access_token: ACCESS_TOKEN,
          account_id: ACCOUNT_ID,
          auth_token: authToken || '',
          res_model: 'dais.health.daily.plan',
          res_id: dailyPlanId,
          res_method: 'add_record_to_daily_plan',
          res_params: {
            // value_options DEBE ser lista (el backend valida type=list);
            // Flutter lo envuelve vía parseAnswerHealthRegister.
            answers: [{ question_id: questionId, value_options: [answerId] }],
          },
        },
      }),
    });
    const json = await res.json();
    const result = json?.result ?? json;
    return { success: result?.success === true };
  } catch (error) {
    console.warn('[healthService] addRecordToDailyPlan error:', error);
    return { success: false };
  }
};

/**
 * Secuencia completa de registro (paso 1 + paso 2). Maneja errores con gracia.
 * @returns {Promise<{success:boolean}>}
 */
export const registerEmotion = async ({ userId, cardId, questionId, answerId, authToken }) => {
  const dailyPlanId = await getDailyPlanId({ userId, cardId, authToken });
  if (dailyPlanId == null) return { success: false };
  return addRecordToDailyPlan({ dailyPlanId, questionId, answerId, authToken });
};
